// Global Instructions Rule Applied!

import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Menu, Typography } from 'antd'
import {
  ApiOutlined,
  AppstoreOutlined,
  BankOutlined,
  BlockOutlined,
  ClusterOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  FilterOutlined,
  InboxOutlined,
  LinkOutlined,
  MailOutlined,
  PartitionOutlined,
  ProfileOutlined,
  ProjectOutlined,
  RobotOutlined,
  SafetyOutlined,
  TagsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import type { GlobalConfigsState } from '../../../state_manager/interfaces/global_configs.ts'
import IndustriesPanel from './IndustriesPanel.tsx'
import PipelinesPanel from './PipelinesPanel.tsx'
import PipelineStagesPanel from './PipelineStagesPanel.tsx'
import PipelineStatusesPanel from './PipelineStatusesPanel.tsx'
import FundStatusesPanel from './FundStatusesPanel.tsx'
import LookupListsPanel from './LookupListsPanel.tsx'
import ContactRelationshipsPanel from './ContactRelationshipsPanel.tsx'
import AccountOwnerRolesPanel from './AccountOwnerRolesPanel.tsx'
import RelationshipFactTypesPanel from './RelationshipFactTypesPanel.tsx'
import StageActionsPanel from './StageActionsPanel.tsx'
import DealExecutionPanel from './DealExecutionPanel.tsx'
import DealOriginationPanel from './DealOriginationPanel.tsx'
import PipelineBudgetAccountsPanel from './PipelineBudgetAccountsPanel.tsx'
import DocumentsPanel from './DocumentsPanel.tsx'
import GLAccountsPanel from './GLAccountsPanel.tsx'
import ChartTemplatesPanel from './ChartTemplatesPanel.tsx'
import InternalAgentsPanel from './InternalAgentsPanel.tsx'
import FollowerAgentsPanel from './FollowerAgentsPanel.tsx'
import AgentGatewayPanel from './AgentGatewayPanel.tsx'
import AgentAccessAdminPanel from './AgentAccessAdminPanel.tsx'
import WaterfallTemplatesPanel from './WaterfallTemplatesPanel.tsx'
import StatementTypesPanel from './StatementTypesPanel.tsx'
import UsersRolesPanel from './UsersRolesPanel.tsx'
import ConnectAgentPanel from './ConnectAgentPanel.tsx'
import EmailCategoriesPanel from './EmailCategoriesPanel.tsx'
import InteractionTrackingPanel from './InteractionTrackingPanel.tsx'
import DocusignEnvelopesPanel from './DocusignEnvelopesPanel.tsx'

const { Title } = Typography

// Registry of config sections. Adding a new managed config list later is just a
// new entry here (key + label + the panel component) — the shell renders the
// left nav and the selected panel generically.
export interface ConfigSection {
  key: string
  label: string
  icon: React.ReactNode
  /** ADMIN ONLY (cfg.app_admins via globalConfigs.is_app_admin). Every section
   *  except Industries is application administration: pipelines, automation,
   *  the GL catalogue, the deal-document rules. Industries is ordinary CRM
   *  reference data that any user maintains, so it stays open.
   *
   *  Hiding rather than disabling matches the Connected Agents precedent — a
   *  visible-but-dead admin control invites support questions. The real
   *  enforcement is the _require_app_admin() guard on the /config/* routes,
   *  so a non-admin who reached a panel some other way collects 403s. */
  adminOnly?: boolean
  render: () => React.ReactNode
}

export const CONFIG_SECTIONS: ConfigSection[] = [
  {
    key: 'industries',
    label: 'Industries',
    icon: <AppstoreOutlined />,
    render: () => <IndustriesPanel />
  },
  {
    key: 'pipelines',
    label: 'Pipelines',
    icon: <PartitionOutlined />,
    adminOnly: true,
    render: () => <PipelinesPanel />
  },
  {
    key: 'pipeline-stages',
    label: 'Pipeline Stages',
    icon: <BlockOutlined />,
    adminOnly: true,
    render: () => <PipelineStagesPanel />
  },
  {
    key: 'pipeline-statuses',
    label: 'Pipeline Statuses',
    icon: <TagsOutlined />,
    adminOnly: true,
    render: () => <PipelineStatusesPanel />
  },
  {
    // FUNDS category (fund status, owner 2026-09-04). The two independent
    // lifecycle clocks a fund runs — fundraising and investment.
    key: 'fund-statuses',
    label: 'Fund Statuses',
    icon: <DollarOutlined />,
    adminOnly: true,
    render: () => <FundStatusesPanel />
  },
  {
    // FUNDS: the shapes a fund's distribution waterfall is created from
    // (docs/WATERFALL_CARRY_DESIGN.md; owner 2026-10-12)
    key: 'waterfall-templates',
    label: 'Waterfall Templates',
    icon: <ProfileOutlined />,
    adminOnly: true,
    render: () => <WaterfallTemplatesPanel />
  },
  {
    // FUNDS: the documents a statement run produces and the report that
    // prints each - the v1 / v2 deck switch without a migration
    // (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md section i)
    key: 'statement-types',
    label: 'Statement Types',
    icon: <FileDoneOutlined />,
    adminOnly: true,
    render: () => <StatementTypesPanel />
  },
  {
    key: 'lookup-lists',
    label: 'Lookup Lists',
    icon: <UnorderedListOutlined />,
    adminOnly: true,
    render: () => <LookupListsPanel />
  },
  {
    key: 'contact-relationships',
    label: 'Contact Relationships',
    icon: <TeamOutlined />,
    adminOnly: true,
    render: () => <ContactRelationshipsPanel />
  },
  {
    key: 'account-owner-roles',
    label: 'Account Owner Roles',
    icon: <TeamOutlined />,
    adminOnly: true,
    render: () => <AccountOwnerRolesPanel />
  },
  {
    key: 'relationship-fact-types',
    label: 'Relationship Fact Types',
    icon: <TeamOutlined />,
    adminOnly: true,
    render: () => <RelationshipFactTypesPanel />
  },
  {
    key: 'stage-actions',
    label: 'Stage Actions',
    icon: <ThunderboltOutlined />,
    adminOnly: true,
    render: () => <StageActionsPanel />
  },
  {
    key: 'deal-origination',
    label: 'Deal Origination',
    icon: <FilterOutlined />,
    adminOnly: true,
    render: () => <DealOriginationPanel />
  },
  {
    key: 'pipeline-budget',
    label: 'Pipeline Budget',
    icon: <DollarOutlined />,
    adminOnly: true,
    render: () => <PipelineBudgetAccountsPanel />
  },
  {
    key: 'deal-execution',
    label: 'Deal Execution',
    icon: <ProjectOutlined />,
    adminOnly: true,
    render: () => <DealExecutionPanel />
  },
  {
    key: 'deal-documents',
    label: 'Deal Documents',
    icon: <FileTextOutlined />,
    adminOnly: true,
    render: () => <DocumentsPanel />
  },
  {
    key: 'gl-accounts',
    label: 'GL Accounts',
    icon: <BankOutlined />,
    adminOnly: true,
    render: () => <GLAccountsPanel />
  },
  {
    key: 'chart-templates',
    label: 'Chart Templates',
    icon: <ProfileOutlined />,
    adminOnly: true,
    render: () => <ChartTemplatesPanel />
  },
  {
    // USERS & ACCESS: roles, their members and the rights matrix
    // (docs/USER_RIGHTS_DESIGN.md section 7)
    key: 'users-roles',
    label: 'Users & Roles',
    icon: <SafetyOutlined />,
    adminOnly: true,
    render: () => <UsersRolesPanel />
  },
  {
    key: 'connect-agent',
    label: 'Connect an Agent',
    icon: <LinkOutlined />,
    // NOT admin-only: this is the connect flow every user needs, and it is the
    // one agent surface reachable before you have a grant.
    adminOnly: false,
    render: () => <ConnectAgentPanel />
  },
  {
    key: 'internal-agents',
    label: 'Internal Agents',
    icon: <RobotOutlined />,
    adminOnly: true,
    render: () => <InternalAgentsPanel />
  },
  {
    key: 'follower-agents',
    label: 'Follower Agents',
    icon: <ClusterOutlined />,
    adminOnly: true,
    render: () => <FollowerAgentsPanel />
  },
  {
    key: 'agent-gateway',
    label: 'Agent Gateway',
    icon: <ApiOutlined />,
    adminOnly: true,
    render: () => <AgentGatewayPanel />
  },
  {
    key: 'agent-access',
    label: 'Agent Oversight',
    icon: <TeamOutlined />,
    adminOnly: true,
    render: () => <AgentAccessAdminPanel />
  },
  {
    key: 'email-categories',
    label: 'Email Categories',
    icon: <MailOutlined />,
    adminOnly: true,
    render: () => <EmailCategoriesPanel />
  },
  {
    key: 'interaction-tracking',
    label: 'Interaction Tracking',
    icon: <InboxOutlined />,
    adminOnly: true,
    render: () => <InteractionTrackingPanel />
  },
  {
    key: 'docusign-envelopes',
    label: 'DocuSign Envelopes',
    icon: <FileDoneOutlined />,
    adminOnly: true,
    render: () => <DocusignEnvelopesPanel />
  }
]

interface ConfigurationPageProps {
  /** Section keys hidden for this menu instance (per-area Configuration:
   *  the Fund Management copy hides 'pipelines'; the Operations copy shows
   *  everything). */
  hiddenSections?: string[]
}

export default function ConfigurationPage({ hiddenSections }: ConfigurationPageProps) {
  // Fail closed: anything other than an explicit true hides the admin sections.
  const isAppAdmin = useSelector(
    (state: { globalConfigs: GlobalConfigsState }) => state.globalConfigs?.is_app_admin === true
  )

  const sections = useMemo(
    () =>
      CONFIG_SECTIONS.filter(
        (s) => !hiddenSections?.includes(s.key) && (isAppAdmin || !s.adminOnly)
      ),
    [hiddenSections, isAppAdmin]
  )
  const [selectedKey, setSelectedKey] = useState<string>(sections[0]?.key ?? '')

  // is_app_admin arrives with the global-configs boot payload, so the section
  // list can shrink under a selection made a moment earlier. Fall back to the
  // first surviving section rather than rendering nothing.
  useEffect(() => {
    if (sections.length && !sections.some((s) => s.key === selectedKey)) {
      setSelectedKey(sections[0].key)
    }
  }, [sections, selectedKey])

  const active = useMemo(
    () => sections.find((s) => s.key === selectedKey) ?? sections[0],
    [sections, selectedKey]
  )

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex bg-white overflow-hidden'>
        {/* Left: config category nav */}
        <div
          className='flex-shrink-0 border-r border-gray-200 flex flex-col'
          style={{ width: 220 }}
        >
          <div className='px-4 py-3 border-b border-gray-200'>
            <Title level={4} style={{ margin: 0 }}>
              Configuration
            </Title>
          </div>
          <Menu
            mode='inline'
            selectedKeys={[selectedKey]}
            onClick={({ key }) => setSelectedKey(String(key))}
            items={sections.map((s) => ({ key: s.key, label: s.label, icon: s.icon }))}
            style={{ borderInlineEnd: 'none', flex: 1 }}
          />
        </div>

        {/* Right: selected panel */}
        <div className='flex-1 min-w-0 flex flex-col px-2 sm:px-4 py-2 sm:py-4 overflow-hidden'>
          {active ? (
            active.render()
          ) : (
            <div className='text-sm text-gray-400 py-6'>
              No configuration sections are available to you.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
